import { useState, useEffect } from 'react'

interface MobileDetectionResult {
  isMobile: boolean
  isTablet: boolean
  deviceType: 'mobile' | 'tablet' | 'desktop'
}

export const useMobileDetection = (): MobileDetectionResult => {
  const [detection, setDetection] = useState<MobileDetectionResult>({
    isMobile: false,
    isTablet: false,
    deviceType: 'desktop'
  })

  useEffect(() => {
    const detectDevice = () => {
      // Get basic info
      const userAgent = navigator.userAgent || ''
      const viewportWidth = window.innerWidth
      const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0

      // Debug logging - disabled for production
      // console.log('[Mobile Detection]', {
      //   userAgent,
      //   viewportWidth,
      //   hasTouchSupport
      // })

      // Simplified mobile detection - prioritize user agent
      const isMobileUA = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)
      const isTabletUA = /iPad|Android.*Tablet/i.test(userAgent)
      
      // Screen size fallback
      const isMobileScreen = viewportWidth < 768 && hasTouchSupport
      const isTabletScreen = viewportWidth >= 768 && viewportWidth <= 1024 && hasTouchSupport

      let isMobile = false
      let isTablet = false
      let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop'

      // iPhone/Android phones - always mobile regardless of screen size
      if (isMobileUA && !isTabletUA) {
        isMobile = true
        deviceType = 'mobile'
        // console.log('[Mobile Detection] Detected as mobile via UA:', userAgent)
      } 
      // iPad/Android tablets
      else if (isTabletUA || (userAgent.includes('Mac') && hasTouchSupport && viewportWidth <= 1024)) {
        isTablet = true
        deviceType = 'tablet'
        // console.log('[Mobile Detection] Detected as tablet via UA:', userAgent)
      }
      // Fallback to screen size detection
      else if (isMobileScreen) {
        isMobile = true
        deviceType = 'mobile'
        // console.log('[Mobile Detection] Detected as mobile via screen size:', viewportWidth)
      }
      else if (isTabletScreen) {
        isTablet = true
        deviceType = 'tablet'
        // console.log('[Mobile Detection] Detected as tablet via screen size:', viewportWidth)
      }
      else {
        deviceType = 'desktop'
        // console.log('[Mobile Detection] Detected as desktop')
      }

      setDetection({
        isMobile,
        isTablet,
        deviceType
      })
    }

    // Initial detection
    detectDevice()

    // Re-detect on window resize (for orientation changes)
    const handleResize = () => {
      detectDevice()
    }

    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
    }
  }, [])

  return detection
}